import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  SortAsc,
  SortDesc,
  Edit,
  Eye,
  Download,
  RefreshCw,
  MoreVertical,
  Calendar,
  FileText,
  X,
  AlertTriangle,
  Clock,
  User,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format, isValid, parseISO } from "date-fns";
import {
  getEmployees,
  type Employee,
} from "@/services/employee.service";
import { useAuthStore } from "@/stores/authStore";
import { PERMISSIONS } from "@/types/permissions";
import { Compensation } from "@/types/models";
import { EditCompensationDialog } from "@/components/compensation/EditCompensationDialog";
import { ViewCompensationDialog } from "@/components/compensation/ViewCompensationDialog";

// Compensation status options
const COMPENSATION_STATUS_OPTIONS = [
  "All",
  "Active",
  "Draft",
  "Upcoming",
  "Expiring Soon",
  "Expired",
];

export default function CompensationPage() {
  const { hasPermission } = useAuthStore();
  const navigate = useNavigate();


  // State management
  const [compensations, setCompensations] = useState<Compensation[]>([]);
  const [allCompensations, setAllCompensations] = useState<Compensation[]>([]);
  const [allCompensationsHistory, setAllCompensationsHistory] = useState<Compensation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [viewMode, setViewMode] = useState<"all" | "grouped">("all");
  const [filterMonth, setFilterMonth] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "endDate", direction: "asc" });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingCompensation, setEditingCompensation] = useState<Compensation | null>(null);
  const [viewingCompensation, setViewingCompensation] = useState<Compensation | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [totalCompensations, setTotalCompensations] = useState(0);


  // Helper function to check if compensation needs notification
  const needsNotification = (compensation: Compensation) => {
    if (!compensation.endDate || !compensation.notifyPeriodInDays) {
      return false;
    }

    const today = new Date();
    const endDate = new Date(compensation.endDate);
    const notifyDays = parseInt(compensation.notifyPeriodInDays, 10);

    if (isNaN(notifyDays)) {
      return false;
    }

    // Calculate notification date (end date - notify days)
    const notificationDate = new Date(endDate);
    notificationDate.setDate(notificationDate.getDate() - notifyDays);

    // Check if today is within the notification period
    return today >= notificationDate && today <= endDate;
  };

  // Helper function to get compensation status
  const getCompensationStatus = (compensation: Compensation) => {
    const today = new Date();
    const startDate = compensation.startDate ? new Date(compensation.startDate) : null;
    const endDate = compensation.endDate ? new Date(compensation.endDate) : null;

    if (!startDate) {
      return { status: 'draft', label: 'Draft', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }

    if (today < startDate) {
      return { status: 'upcoming', label: 'Upcoming', color: 'bg-blue-50 text-blue-700 border-blue-200' };
    }

    if (endDate && today > endDate) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-700 border-red-200' };
    }

    // Check if compensation needs notification
    if (needsNotification(compensation)) {
      return { status: 'notification', label: 'Expiring Soon', color: 'bg-orange-50 text-orange-700 border-orange-200' };
    }

    return { status: 'active', label: 'Active', color: 'bg-green-50 text-green-700 border-green-200' };
  };

  // Helper function to process compensations from employee data
  const processCompensationsFromEmployees = (employees: Employee[]): Compensation[] => {
    const allCompensations: Compensation[] = [];

    employees.forEach(employee => {
      if (employee.compensationInfo && Array.isArray(employee.compensationInfo)) {
        // Sort compensations by start date (newest first) to get the latest
        const sortedCompensations = employee.compensationInfo.sort((a: any, b: any) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB.getTime() - dateA.getTime();
        });

        // Only take the first (latest) compensation for individual view
        if (sortedCompensations.length > 0) {
          const latestCompensation = sortedCompensations[0];
          allCompensations.push({
            _id: `${employee._id}-${latestCompensation.startDate}-${latestCompensation.endDate}`, // Generate unique ID
            startDate: latestCompensation.startDate,
            endDate: latestCompensation.endDate,
            newSalary: latestCompensation.newSalary || '',
            payFrequency: latestCompensation.payFrequency || '',
            noticePeriodInDays: latestCompensation.noticePeriodInDays || '',
            notifyPeriodInDays: latestCompensation.notifyPeriodInDays || '',
            salaryMode: latestCompensation.salaryMode || '',
            note: latestCompensation.note || '',
            employee: {
              _id: employee._id || '',
              user: {
                firstName: employee.user?.firstName || '',
                lastName: employee.user?.lastName || '',
                email: employee.user?.email || '',
                employeeId: employee.employeeId || '',
              },
              department: employee.department,
            },
          });
        }
      }
    });

    return allCompensations;
  };

  // Helper function to process all compensations from employee data (for grouped view)
  const processAllCompensationsFromEmployees = (employees: Employee[]): Compensation[] => {
    const allCompensations: Compensation[] = [];

    employees.forEach(employee => {
      if (employee.compensationInfo && Array.isArray(employee.compensationInfo)) {
        // Sort compensations by start date (newest first) to show history
        const sortedCompensations = employee.compensationInfo.sort((a: any, b: any) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB.getTime() - dateA.getTime();
        });

        sortedCompensations.forEach((compensation, index) => {
          allCompensations.push({
            _id: `${employee._id}-${compensation.startDate}-${compensation.endDate}-${index}`, // Generate unique ID with index
            startDate: compensation.startDate,
            endDate: compensation.endDate,
            newSalary: compensation.newSalary || '',
            payFrequency: compensation.payFrequency || '',
            noticePeriodInDays: compensation.noticePeriodInDays || '',
            notifyPeriodInDays: compensation.notifyPeriodInDays || '',
            salaryMode: compensation.salaryMode || '',
            note: compensation.note || '',
            employee: {
              _id: employee._id || '',
              user: {
                firstName: employee.user?.firstName || '',
                lastName: employee.user?.lastName || '',
                email: employee.user?.email || '',
                employeeId: employee.employeeId || '',
              },
              department: employee.department,
            },
          });
        });
      }
    });

    return allCompensations;
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const employeesData = await getEmployees();

        if (!Array.isArray(employeesData)) {
          setAllCompensations([]);
          setAllCompensationsHistory([]);
          return;
        }

        const latestCompensations = processCompensationsFromEmployees(employeesData);
        const allCompensationsData = processAllCompensationsFromEmployees(employeesData);

        setAllCompensations(latestCompensations);
        setAllCompensationsHistory(allCompensationsData);
        setLoading(false);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch compensation data.",
          variant: "destructive",
          duration: 1000,
        });
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter and sort data
  useEffect(() => {
    // Use appropriate data source based on view mode
    const sourceData = viewMode === "grouped" ? allCompensationsHistory : allCompensations;

    let filteredCompensations = sourceData.filter((compensation: Compensation) => {
      const matchesSearch =
        !searchTerm ||
        (compensation.employee.user.firstName &&
          compensation.employee.user.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (compensation.employee.user.lastName &&
          compensation.employee.user.lastName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (compensation.employee.user.email &&
          compensation.employee.user.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (compensation.employee.user.employeeId &&
          compensation.employee.user.employeeId
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));

      const statusInfo = getCompensationStatus(compensation);
      const matchesStatus = (() => {
        if (selectedStatus === "All") return true;

        // Map UI status names to internal status values
        const statusMap: { [key: string]: string } = {
          "Active": "active",
          "Draft": "draft",
          "Upcoming": "upcoming",
          "Expiring Soon": "notification",
          "Expired": "expired"
        };

        const expectedStatus = statusMap[selectedStatus];
        return expectedStatus ? statusInfo.status === expectedStatus : false;
      })();

      // Month filtering - show only compensations that expire in the selected month
      let matchesMonthFilter = true;
      if (filterMonth) {
        const selectedDate = new Date(filterMonth);
        const compensationEndDate = new Date(compensation.endDate);

        // Get the first and last day of the selected month
        const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

        // Check if compensation expires in the selected month
        const expiresInSelectedMonth = compensationEndDate >= monthStart && compensationEndDate <= monthEnd;

        matchesMonthFilter = expiresInSelectedMonth;
      }

      return matchesSearch && matchesStatus && matchesMonthFilter;
    });

    // Sort data
    filteredCompensations.sort((a: Compensation, b: Compensation) => {
      let aValue: any = a[sortConfig.key as keyof Compensation];
      let bValue: any = b[sortConfig.key as keyof Compensation];

      // Special handling for dates
      if (sortConfig.key === 'endDate' || sortConfig.key === 'startDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortConfig.direction === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // Pagination
    setTotalCompensations(filteredCompensations.length);
    const totalPages = Math.ceil(filteredCompensations.length / pageSize);
    const newCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    const startIndex = (newCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedCompensations = filteredCompensations.slice(startIndex, endIndex);

    setCompensations(paginatedCompensations);

    if (currentPage !== newCurrentPage) {
      setCurrentPage(newCurrentPage);
    }
  }, [
    allCompensations,
    allCompensationsHistory,
    searchTerm,
    selectedStatus,
    filterMonth,
    sortConfig,
    currentPage,
    pageSize,
    viewMode,
  ]);

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Actions handlers
  const handleView = (compensation: Compensation) => {
    setViewingCompensation(compensation);
    setIsViewDialogOpen(true);
  };

  const handleEdit = (compensation: Compensation) => {
    setEditingCompensation(compensation);
    setIsEditDialogOpen(true);
  };

  const handleViewProfile = (compensation: Compensation) => {
    navigate(`/employee/${compensation.employee._id}`);
  };

  const handleEditSuccess = async () => {
    // Refresh the compensation list after successful edit
    try {
      const employeesData = await getEmployees();
      const latestCompensations = processCompensationsFromEmployees(employeesData);
      const allCompensationsData = processAllCompensationsFromEmployees(employeesData);

      setAllCompensations(latestCompensations);
      setAllCompensationsHistory(allCompensationsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh data after edit",
        variant: "destructive",
        duration: 1000,
      });
    }
  };

  // Refresh data handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const employeesData = await getEmployees();
      const latestCompensations = processCompensationsFromEmployees(employeesData);
      const allCompensationsData = processAllCompensationsFromEmployees(employeesData);

      setAllCompensations(latestCompensations);
      setAllCompensationsHistory(allCompensationsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
        duration: 1000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const data = await getEmployees();
      const processedCompensations = viewMode === "grouped"
        ? processAllCompensationsFromEmployees(data)
        : processCompensationsFromEmployees(data);

      // Filter data for export based on current view
      const filteredData = processedCompensations.filter((compensation: Compensation) => {
        const matchesSearch =
          !searchTerm ||
          compensation.employee.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          compensation.employee.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          compensation.employee.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          compensation.employee.user.employeeId.toLowerCase().includes(searchTerm.toLowerCase());

        const statusInfo = getCompensationStatus(compensation);
        const matchesStatus =
          selectedStatus === "All" || statusInfo.status === selectedStatus.toLowerCase().replace(' ', '_');

        return matchesSearch && matchesStatus;
      });

      // Sort by end date
      filteredData.sort((a, b) => {
        const dateA = new Date(a.endDate).getTime();
        const dateB = new Date(b.endDate).getTime();
        return dateA - dateB;
      });

      // Create CSV content
      const csvContent = [
        // Header
        [
          "Employee ID",
          "Employee Name",
          "Email",
          "Department",
          "Start Date",
          "End Date",
          "New Salary",
          "Pay Frequency",
          "Salary Mode",
          "Status",
          "Note"
        ].join(","),
        // Data rows
        ...filteredData.map(compensation => {
          const statusInfo = getCompensationStatus(compensation);
          const departmentName = typeof compensation.employee.department === "object"
            ? compensation.employee.department?.name || "N/A"
            : compensation.employee.department || "N/A";

          return [
            compensation.employee.user.employeeId,
            `"${compensation.employee.user.firstName} ${compensation.employee.user.lastName}"`,
            compensation.employee.user.email,
            `"${departmentName}"`,
            format(new Date(compensation.startDate), "yyyy-MM-dd"),
            format(new Date(compensation.endDate), "yyyy-MM-dd"),
            compensation.newSalary,
            compensation.payFrequency,
            compensation.salaryMode,
            statusInfo.label,
            `"${compensation.note || ""}"`
          ].join(",");
        })
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `compensations_${format(new Date(), "yyyy-MM-dd")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Success",
        description: "Compensations data has been exported",
        duration: 1000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive",
        duration: 1000,
      });
    }
  };


  // Calculate total pages
  const totalPages = Math.ceil(totalCompensations / pageSize);

  // Helper function to group compensations by employee
  const groupCompensationsByEmployee = (compensations: Compensation[]) => {
    const grouped = compensations.reduce((acc, compensation) => {
      const employeeId = compensation.employee._id;
      if (!acc[employeeId]) {
        acc[employeeId] = {
          employee: compensation.employee,
          compensations: []
        };
      }
      acc[employeeId].compensations.push(compensation);
      return acc;
    }, {} as Record<string, { employee: Compensation['employee']; compensations: Compensation[] }>);

    return Object.values(grouped);
  };


  return (
    <div className="space-y-4 p-4 pt-1 pb-10">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Compensation Management
            </h1>
          </div>
        </div>

        {/* Stats Cards */}
        {allCompensations.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Total Compensations
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allCompensations.length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Active Compensations
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allCompensations.filter(c => getCompensationStatus(c).status === 'active').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-orange-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Expiring Soon
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allCompensations.filter(c => getCompensationStatus(c).status === 'expiring_soon').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
                    <X className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Expired
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allCompensations.filter(c => getCompensationStatus(c).status === 'expired').length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Card */}
        <Card className="border border-gray-200 shadow-sm rounded-lg mb-6 bg-white">
          <CardHeader className="pb-2"> </CardHeader>

          <CardContent className="pt-0">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by employee name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1 h-6 w-6"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Status Filter */}
              <Select
                value={selectedStatus}
                onValueChange={setSelectedStatus}
              >
                <SelectTrigger className="h-10 w-32 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {COMPENSATION_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Month Filter */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="month"
                  placeholder="Select Month"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  onClick={(e) => {
                    e.currentTarget.focus();
                    if (e.currentTarget.showPicker) {
                      e.currentTarget.showPicker();
                    }
                  }}
                  onFocus={(e) => {
                    if (e.currentTarget.showPicker) {
                      e.currentTarget.showPicker();
                    }
                  }}
                  className="pl-10 h-10 w-40 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white cursor-pointer"
                />
              </div>

              {/* View Mode */}
              <Select
                value={viewMode}
                onValueChange={(value: "all" | "grouped") => setViewMode(value)}
              >
                <SelectTrigger className="h-10 w-40 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <FileText className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Select view mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Individual Records
                  </SelectItem>
                  <SelectItem value="grouped">
                    Employee History
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedStatus("All");
                    setFilterMonth("");
                  }}
                  className="h-10 w-10"
                  title="Clear Filters"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleExport}
                  size="icon"
                  className="h-10 w-10"
                  title="Download CSV"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compensation List */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {viewMode === "grouped" ? "Employee Compensation History" : "Compensation Records"}
                </CardTitle>

              </div>
              <div className="flex items-center space-x-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(v) => setPageSize(Number(v))}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Rows per page" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50].map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size} rows
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""
                            }`}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh Data</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("employee.user.firstName")}
                    >
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Employee</span>
                        {sortConfig.key === "employee.user.firstName" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Department</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("startDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Start Date</span>
                        {sortConfig.key === "startDate" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("endDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">End Date</span>
                        {sortConfig.key === "endDate" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("newSalary")}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="text-xs sm:text-sm">Salary</span>
                        {sortConfig.key === "newSalary" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Status</TableHead>
                    <TableHead className="text-right text-xs sm:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          <Skeleton className="h-5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-20 rounded-md" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-8 w-8 ml-auto" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : isRefreshing ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">
                            Loading compensations...
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : compensations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <span className="text-sm text-muted-foreground">
                            No compensations found
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchTerm("");
                              setSelectedStatus("All");
                              setFilterMonth("");
                            }}
                          >
                            Clear Filters
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : viewMode === "grouped" ? (
                    // Grouped view - show employee groups
                    groupCompensationsByEmployee(compensations).map((group) => (
                      <React.Fragment key={group.employee._id}>
                        {/* Employee header row */}
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={7} className="font-semibold text-gray-900 py-3">
                            <div className="flex items-center gap-3">
                              <User className="h-4 w-4" />
                              <span>
                                {group.employee.user.firstName} {group.employee.user.lastName}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {group.compensations.length} compensation{group.compensations.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                        {/* Compensation rows for this employee */}
                        {group.compensations.map((compensation, index) => {
                          const statusInfo = getCompensationStatus(compensation);
                          const departmentName = typeof compensation.employee.department === "object"
                            ? compensation.employee.department?.name || "N/A"
                            : compensation.employee.department || "N/A";

                          return (
                            <TableRow key={compensation._id} className="border-l-4 border-l-blue-200">
                              <TableCell className="font-medium text-xs sm:text-sm pl-8">
                                <div className="text-gray-500 text-xs">
                                  Compensation #{index + 1}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className="bg-blue-50 text-blue-700 text-xs"
                                >
                                  {departmentName}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {isValid(parseISO(String(compensation.startDate)))
                                  ? format(parseISO(String(compensation.startDate)), "MMM dd, yyyy")
                                  : "N/A"}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {isValid(parseISO(String(compensation.endDate)))
                                  ? format(parseISO(String(compensation.endDate)), "MMM dd, yyyy")
                                  : "N/A"}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {compensation.newSalary || "N/A"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${statusInfo.color}`}
                                >
                                  {statusInfo.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 sm:h-9 sm:w-9"
                                          >
                                            <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                      </TooltipTrigger>
                                      <TooltipContent>Actions</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-[180px]"
                                  >
                                    {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                                      <DropdownMenuItem
                                        onClick={() => handleView(compensation)}
                                      >
                                        <Eye className="mr-2 h-4 w-4" />
                                        View Details
                                      </DropdownMenuItem>
                                    )}
                                    {hasPermission(PERMISSIONS.EMPLOYEES_EDIT) && (
                                      <DropdownMenuItem
                                        onClick={() => handleEdit(compensation)}
                                      >
                                        <Edit className="mr-2 h-4 w-4" />
                                        Edit Compensation
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    // Regular view - show all compensations
                    compensations.map((compensation) => {
                      const statusInfo = getCompensationStatus(compensation);
                      const departmentName = typeof compensation.employee.department === "object"
                        ? compensation.employee.department?.name || "N/A"
                        : compensation.employee.department || "N/A";

                      return (
                        <TableRow key={compensation._id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            <div>
                              <div className="font-semibold">
                                {compensation.employee.user.firstName} {compensation.employee.user.lastName}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {compensation.employee.user.employeeId}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {compensation.employee.user.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 text-xs"
                            >
                              {departmentName}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {isValid(parseISO(String(compensation.startDate)))
                              ? format(parseISO(String(compensation.startDate)), "MMM dd, yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {isValid(parseISO(String(compensation.endDate)))
                              ? format(parseISO(String(compensation.endDate)), "MMM dd, yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {compensation.newSalary || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`text-xs ${statusInfo.color}`}
                            >
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 sm:h-9 sm:w-9"
                                      >
                                        <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent>Actions</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <DropdownMenuContent
                                align="end"
                                className="w-[180px]"
                              >
                                {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                                  <DropdownMenuItem
                                    onClick={() => handleView(compensation)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </DropdownMenuItem>
                                )}
                                {hasPermission(PERMISSIONS.COMPENSATION_EDIT) && (
                                  <DropdownMenuItem
                                    onClick={() => handleViewProfile(compensation)}
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    View Profile
                                  </DropdownMenuItem>
                                )}
                                {hasPermission(PERMISSIONS.COMPENSATION_EDIT) && (
                                  <DropdownMenuItem
                                    onClick={() => handleEdit(compensation)}
                                  >
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Compensation
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {!loading && !isRefreshing && compensations.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalCompensations)} of{" "}
                  {totalCompensations} entries
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="h-8 px-3 text-sm"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="default"
                    className="h-8 w-8 p-0 text-sm bg-[#000000] hover:bg-[#000000] text-white"
                    disabled
                  >
                    {currentPage}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-8 px-3 text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      {editingCompensation && (
        <EditCompensationDialog
          compensation={editingCompensation}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSuccess={handleEditSuccess}
        />
      )}
      {viewingCompensation && (
        <ViewCompensationDialog
          compensation={viewingCompensation}
          open={isViewDialogOpen}
          onOpenChange={setIsViewDialogOpen}
        />
      )}
    </div>
  );
}